import React, { useState } from 'react';
import { toast } from 'react-toastify';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import Navbar, { BrandIcon } from '../components/Navbar';
import { getApiUrl } from '../api';
import styles from './ContactPage.module.css';

const SUPPORT_EMAIL = 'support@replyshoot.com';

const ContactPage = () => {
  const { t } = useLanguage();
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const payload = Object.fromEntries(data.entries());

    setIsSending(true);
    try {
      const response = await fetch(getApiUrl('/api/contact'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to send your message.');
      }

      toast.success(t('contactSuccess', 'Your message has been sent. We’ll be in touch soon.'));
      form.reset();
    } catch (error) {
      toast.error(error.message || t('contactError', 'Could not send your message. Please try again.'));
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={styles.page}>
      <Navbar
        commonNavigation
        className={styles.navbar}
        innerClassName={styles.navInner}
        brandClassName={styles.navBrand}
        brand={<BrandIcon />}
      />

      <main className={styles.contactSection}>
        <div className={styles.contactContainer}>
          <section className={styles.contactInfo}>
            <p className={styles.eyebrow}>{t('contact', 'Contact')}</p>
            <h1>{t('contactTitle', 'Get in touch')}</h1>
            <p className={styles.intro}>
              {t('contactIntro', 'Have a question about ReplyShoot? Send us a message and we’ll be happy to help.')}
            </p>

            <div className={styles.infoCard}>
              <span className={styles.infoIcon} aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="m3 7 9 6 9-6" />
                </svg>
              </span>
              <div>
                <h2>{t('contactEmailTitle', 'Email support')}</h2>
                <p>{t('contactEmailDescription', 'Reach our team directly at')}</p>
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
              </div>
            </div>
          </section>

          <section className={styles.formCard} aria-labelledby="contact-form-title">
            <h2 id="contact-form-title">{t('contactFormTitle', 'Send us a message')}</h2>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label htmlFor="contact-name">{t('contactName', 'Full name')}</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  placeholder={t('contactNamePlaceholder', 'Enter your name')}
                  minLength="3"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="contact-email">{t('contactEmail', 'Email address')}</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder={t('contactEmailPlaceholder', 'you@example.com')}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="contact-subject">{t('contactSubject', 'Subject')}</label>
                <input
                  id="contact-subject"
                  name="subject"
                  type="text"
                  placeholder={t('contactSubjectPlaceholder', 'How can we help?')}
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="contact-message">{t('contactMessage', 'Message')}</label>
                <textarea
                  id="contact-message"
                  name="message"
                  placeholder={t('contactMessagePlaceholder', 'Tell us how we can help...')}
                  minLength="10"
                  required
                />
              </div>
              <button type="submit" className={styles.submitButton} disabled={isSending}>
                {isSending ? t('contactSending', 'Sending...') : t('contactSend', 'Send message')}
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m22 2-7 20-4-9-9-4Z" />
                  <path d="M22 2 11 13" />
                </svg>
              </button>
              <p className={styles.formNote}>
                {t('contactEmailClientNote', 'Your message will be emailed directly to our support team.')}
              </p>
            </form>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ContactPage;
